import React from 'react';
import { Card, Progress, Spinner } from '@/components/ui';
import { useUsageData } from '@/api/account';

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function usagePercent(used: number, limit: number): number {
    if (limit === 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
}

function progressColor(percent: number): string {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-primary';
}

const PlanUsage: React.FC = () => {
    const { data, loading, error } = useUsageData();

    return (
        <Card
            header={{
                content: <h2 className="text-lg font-semibold">Plan &amp; Usage</h2>,
                bordered: true,
            }}
        >
            {loading && (
                <div className="flex justify-center py-6">
                    <Spinner size={32} enableTheme />
                </div>
            )}

            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}

            {data && (
                <div className="space-y-6">
                    {/* Plan label */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Current plan:</span>
                        <span className="text-sm font-medium px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
                            Free
                        </span>
                    </div>

                    {/* Text storage */}
                    <UsageRow
                        label="Text storage"
                        used={data.usage.text_storage_bytes}
                        limit={data.limits.max_text_storage_bytes}
                    />

                    <UsageRow
                        label="File storage"
                        used={data.usage.file_storage_bytes}
                        limit={data.limits.max_file_storage_bytes}
                    />

                    <div>
                        <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 dark:text-gray-300">Max file size</span>
                            <span className="text-gray-500 dark:text-gray-400">
                                {formatBytes(data.limits.max_file_size_bytes)}
                            </span>
                        </div>
                    </div>

                    {/* Per-note size limit (static info — no usage counter) */}
                    <div>
                        <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 dark:text-gray-300">Max note size</span>
                            <span className="text-gray-500 dark:text-gray-400">
                                {formatBytes(data.limits.max_note_size_bytes)}
                            </span>
                        </div>
                    </div>

                    {/* Version history */}
                    <div>
                        <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 dark:text-gray-300">Version history</span>
                            <span className="text-gray-500 dark:text-gray-400">
                                Up to {data.limits.max_versions_per_note} versions per note
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

interface UsageRowProps {
    label: string;
    used: number;
    limit: number;
}

const UsageRow: React.FC<UsageRowProps> = ({ label, used, limit }) => {
    const percent = usagePercent(used, limit);
    const color = progressColor(percent);

    return (
        <div>
            <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700 dark:text-gray-300">{label}</span>
                <span className="text-gray-500 dark:text-gray-400">
                    {formatBytes(used)} / {formatBytes(limit)}
                </span>
            </div>
            <Progress
                percent={percent}
                showInfo={false}
                customColorClass={color}
            />
            {percent >= 90 && (
                <p className="text-xs text-red-500 mt-1">
                    You are close to your {label.toLowerCase()} limit.
                </p>
            )}
        </div>
    );
};

export default PlanUsage;
